import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { UploadCloud, File as FileIcon, X } from "lucide-react";

/**
 * PDF dropzone with size validation.
 * onChange(File | null)
 */
export const PitchDeckDropzone = ({ file, onChange, maxMb = 25, testId = "pitch-dropzone" }) => {
  const { t } = useTranslation();
  const ref = useRef(null);

  const validate = (f) => {
    if (!f) return null;
    if (!/\.pdf$/i.test(f.name)) return t("submit.deckErrorType");
    if (f.size > maxMb * 1024 * 1024) return t("submit.deckErrorSize", { mb: maxMb });
    return null;
  };

  const handleFiles = (files) => {
    const f = files?.[0];
    if (!f) return;
    const err = validate(f);
    if (err) {
      onChange(null, err);
      return;
    }
    onChange(f, null);
  };

  return (
    <div data-testid={testId}>
      {!file ? (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
          className="w-full flex flex-col items-center justify-center gap-4 border-2 border-dashed border-sandBeige bg-porcelain rounded-3xl px-6 py-12 hover:border-raspberry hover:bg-roseMilk/40 transition-colors"
          data-testid="pitch-dropzone-empty"
        >
          <div className="w-14 h-14 rounded-2xl bg-roseMilk flex items-center justify-center text-raspberry">
            <UploadCloud size={22} strokeWidth={1.6} />
          </div>
          <div className="text-center">
            <p className="font-medium text-cocoa">{t("submit.deckDropTitle")}</p>
            <p className="mt-1 text-xs text-warmGrey">
              {t("submit.deckDropHint", { mb: maxMb })}
            </p>
          </div>
        </button>
      ) : (
        <div
          className="flex items-center gap-4 p-5 bg-white rounded-3xl border border-cardEdge shadow-soft"
          data-testid="pitch-dropzone-filled"
        >
          <div className="w-12 h-12 rounded-xl bg-sageMint flex items-center justify-center text-cocoa">
            <FileIcon size={20} strokeWidth={1.6} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-cocoa truncate" data-testid="pitch-filename">
              {file.name}
            </p>
            <p className="text-xs text-warmGrey">
              {(file.size / (1024 * 1024)).toFixed(2)} MB
            </p>
          </div>
          <button
            type="button"
            onClick={() => onChange(null, null)}
            className="w-9 h-9 rounded-full bg-ivory hover:bg-roseMilk border border-sandBeige text-cocoa flex items-center justify-center transition-colors"
            aria-label={t("submit.deckRemove")}
            data-testid="pitch-remove"
          >
            <X size={15} />
          </button>
        </div>
      )}
      <input
        ref={ref}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="pitch-input"
      />
    </div>
  );
};

export default PitchDeckDropzone;
